/**
 * Confidence-band copy for the Readiness Ring.
 *
 * **The backend owns these cutoffs.** `TrackingService` computes the band
 * server-side from `TRACKING_CONFIG` precisely so a client cannot drift from
 * it, and the band should arrive with the score once the API client lands.
 *
 * This exists as the offline fallback: screens 9, 10, 15, 16 and 19 must keep
 * working without data, and a cached score with no band is a number with no
 * sentence under it. When the two disagree, the server is right.
 *
 * `tests/readinessBandDrift.test.ts` reads the backend's config and fails if
 * these values stop matching, so "keep them in sync" is a check rather than a
 * hope.
 */
export type ReadinessBand =
  "still settling" | "still sharpening" | "early days" | "building" | "exam ready";

/** Mirrors `TRACKING_CONFIG.bands` in the backend. */
export const BAND_THRESHOLDS = {
  settlingBelow: 0.3,
  sharpeningBelow: 0.7,
  /** Score cutoffs, as percentages. */
  earlyDaysBelow: 40,
  buildingBelow: 70,
} as const;

export function readinessBand(confidence: number, score: number): ReadinessBand {
  if (confidence < BAND_THRESHOLDS.settlingBelow) return "still settling";
  if (confidence < BAND_THRESHOLDS.sharpeningBelow) return "still sharpening";
  if (score < BAND_THRESHOLDS.earlyDaysBelow) return "early days";
  if (score < BAND_THRESHOLDS.buildingBelow) return "building";
  return "exam ready";
}
